import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {t} from 'i18next';
import {Box, Grid, Stack, Typography} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  PersistSearches,
  AddUnsavedSearch,
  ClearShoppingCart,
} from '../../redux/slice/sessionSearchSlice';
import './ViewSessionSearch.scss';
import {ResultType, SessionSearch} from './types/SessionSearch';
import LeftHeader from './page-header/left-header/LeftHeader';
import CenterHeader from './page-header/center-header/CenterHeader';
import SearchList from './search-list/SearchList';
import SearchModifiers from './search-modifiers/SearchModifiers';
import SearchQuery from './search-query/SearchQuery';
import SaveSearchDrawer from './save-search-drawer/SaveSearchDrawer';
import {Query, QueryType} from './types/Query';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import {
  buildCriteriaString,
  createSearchRequest,
  createSearchResultRequest,
  getCriteriaValues,
  hasAnyCriteriaAdded,
  toSearchResult,
  isExecuted,
  isSavedSearchLimitExceeded,
  parseSavedSearchLimit,
  toSearchResultRequest,
  createEmptySearchResult,
} from './utilities/utilities';
import {
  mapNestedSearchItemsToGridRows,
  resolveSessionRunSearchFlow,
  getNestedSearchGridHeight,
  NESTED_SEARCH_GRID_MAX_VISIBLE_ROWS,
} from './utilities/nestedSearchUtils';
import {
  useAddNotification,
  useReloadSearchResult,
  useRunSearch,
  useAddSearchToQueue,
  useSearchStatus,
  useValidateAnalyticCriteria,
  useValidateContentmetaData,
  useValidateSearchCriteria,
  useValidateWorkProductCriteria,
  useGetSearchUniqueDocIds,
} from '../../hooks/session-search/useSessionSearch';
import ArrModal from '../common/arr-modal/ArrModal';
import {
  AddSearchMessageToQueueRequest,
  SearchResultRequest,
  SessionSearchItem,
} from '../../api/types/sessionSearch.type';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  SEARCHFAILED_SIGNALR_TYPE,
  SEARCHSUCCESS_SIGNALR_TYPE,
  SessionSearchColors,
} from './utilities/constants';
import {Criteria} from './types/Criteria';
import ResultShoppingCart from './result-shopping-cart/ResultShoppingCart';
import {useLocation} from 'react-router-dom';
import {ArrSearchProgressDialog} from '../common/arr-search-progress-dialog/ArrSearchProgressDialog';
import {SignalREvent} from '../../redux/slice/signalrSlice';
import ArrDataGrid from '../common/arr-data-grid/ArrDataGrid';
import ArrButton from '../common/arr-button/ArrButton';
import ArrBanner from '../common/arr-banner/ArrBanner';
import {DEFAULT_ROW_HEIGHT} from '../common/lazy-load-data-grid/constant';
import {ReactComponent as CompleteIcon} from '../../assets/images/complete-icon.svg';
import {nestedColumns} from '../../api/types/nestedsearch.types';
import {IIndexDropdownResponse} from '../../api/types/searchType.type';
import {NestedChildSearchItem} from '../../api/types/sessionSearch.type';

class DefaultSessionSearch implements SessionSearch {
  id = uuidv4();
  name = '';
  familyMemberDocuments = false;
  threadedEmailDocuments = false;
  nearDuplicateDocuments = false;
  regexOnWorkProductAttributes = false;
  isSelected = false;
  isPinned = false;
  isSaved = false;
  lastRuntime?: Date = undefined;
  advancedSearchId?: number = undefined;
  query: Query[] = [
    {type: QueryType.ContentAndMetadata, value: [], operator: 'AND'},
  ];
}

class SearchValidationAbortError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SearchValidationAbortError';
  }
}

const SEARCH_STATUS_POLL_INTERVAL_MS = 2000;

type SearchWaitPhase = 'initial' | 'extended' | 'final';

const SEARCH_WAIT_DEADLINE_MS: Record<SearchWaitPhase, number> = {
  initial: 8000,
  extended: 30000,
  final: 60000,
};

const isSearchStatusReady = (status?: number) => status === 4 || status === 1;

const getNextSearchWaitPhase = (phase: SearchWaitPhase): SearchWaitPhase =>
  phase === 'initial' ? 'extended' : 'final';

const getIndexDocCount = (index?: IIndexDropdownResponse): number => {
  const count = index?.indexDocCount;
  return typeof count === 'number' && Number.isFinite(count) && count >= 0
    ? count
    : 0;
};

const ViewSessionSearch: React.FC = () => {
  const [sessionSearches, setSessionSearches] = useState<SessionSearch[]>([
    new DefaultSessionSearch(),
  ]);
  const [displaySearches, setDisplaySearches] = useState<SessionSearch[]>([
    new DefaultSessionSearch(),
  ]);
  const [currentSessionSearch, setCurrentSessionSearch] =
    useState<SessionSearch>(new DefaultSessionSearch());
  const hasLoadedFromNavigation = useRef(false);
  const hasInitializedFromRedux = useRef(false);
  const hasHandledBackNavigation = useRef(false);
  const selectionApplied = useRef(false);
  const [uniqueDocIdCount, setUniqueDocIdCount] = useState<number>(0);
  const [savedSearchLimit, setSavedSearchLimit] = useState(-1);
  const savedSearchLimitRef = useRef(-1);
  const queueRequestRef = useRef<AddSearchMessageToQueueRequest | null>(null);
  const userRoleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const projectId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.projectId
  );
  const userSecurityGroup = useSelector(
    (state: RootState) => state.authenticatedUser.user?.securityGroupId
  );

  const [
    showAnalyticWorProductValidation,
    setShowAnalyticWorProductValidation,
  ] = useState<boolean>(false);
  const [showSearchToast, setShowSearchToast] = useState<boolean>(false);
  const [showContentMDValidation, setShowContentMDValidation] =
    useState<boolean>(false);
  const [readyForSearch, setReadyForSearch] = useState<boolean>(false);
  const [canContinueWithSearch, setcanContinueWithSearch] =
    useState<boolean>(false);
  const [criticalValidationError, setCriticalValidationError] =
    useState<string>('');
  const [errorStringContainsHtml, setErrorStringContainsHtml] =
    useState<boolean>(false);
  const [contentMDValidationError, setContentMDValidationError] =
    useState<string>('');
  const [toastType, setToastType] = useState(ToastType.Warning);
  const [searchToastMessages, setSearchToastMessages] = useState<
    {id: number; message: string}[]
  >([]);
  const toastIdRef = useRef(0);
  const showSearchErrorToast = useCallback(
    (messages: string | string[], type: ToastType = ToastType.Error) => {
      const next = Array.isArray(messages) ? messages : [messages];
      setToastType(type);
      setSearchToastMessages(
        next.filter(Boolean).map((message) => ({
          id: ++toastIdRef.current,
          message,
        }))
      );
      setShowSearchToast(true);
    },
    []
  );
  const [currentSearchResultRequest, setCurrentSearchResultRequest] =
    useState<SearchResultRequest | null>(null);
  const [searchIsInProgress, setSearchIsInProgress] = useState<boolean>(false);
  const validateAnalytic = useValidateAnalyticCriteria();
  const validateWorkProduct = useValidateWorkProductCriteria();
  const validateContentMeta = useValidateContentmetaData();
  const validateSearchCriteria = useValidateSearchCriteria();
  const [canAddItem, setCanAddItem] = useState<boolean>(false);
  const [canDiscardItem, setCanDiscardItem] = useState<boolean>(false);
  const [canPinItem, setCanPinItem] = useState<boolean>(true);
  const [currentSessionSearchBackup, setCurrentSessionSearchBackup] =
    useState<SessionSearch | null>(null);
  const [isSaveDrawerOpen, setIsSaveDrawerOpen] = useState<boolean>(false);
  const [showSaveSuccessToast, setShowSaveSuccessToast] =
    useState<boolean>(false);
  const [showPinToast, setShowPinToast] = useState<boolean>(false);
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const [shouldPollResults, setShouldPollResults] = useState(false);
  const [showSearchWaitModal, setShowSearchWaitModal] = useState(false);
  const [searchWaitPhase, setSearchWaitPhase] =
    useState<SearchWaitPhase>('initial');
  const searchWaitStartedAtRef = useRef<number | null>(null);
  const handleNotifyAllRef = useRef<() => void | Promise<void>>(
    () => undefined
  );
  const [nestedChildModal, setNestedChildModal] = useState(false);
  const [sessionSearchItem, setSessionSearchItem] = useState<SessionSearch>();
  const [openNestedSearchModal, setOpenNestedSearchModal] = useState(false);
  const [rows, setRows] = useState<NestedChildSearchItem[]>([]);
  const nestedGridRows = useMemo(
    () => mapNestedSearchItemsToGridRows(rows),
    [rows]
  );
  const nestedGridScrollable =
    nestedGridRows.length > NESTED_SEARCH_GRID_MAX_VISIBLE_ROWS;
  const nestedGridHeight = useMemo(
    () => getNestedSearchGridHeight(nestedGridRows.length, DEFAULT_ROW_HEIGHT),
    [nestedGridRows.length]
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const persistentSearches = useSelector(
    (state: RootState) => state.sessionSearch.persistentSearches
  );
  const signalREvents = useSelector((state: RootState) => state.signalr.events);
  const projectContextKey = `${projectId ?? ''}|${userRoleId ?? ''}|${userSecurityGroup ?? ''}`;
  const lastProjectContextKey = useRef<string | null>(null);
  const searchId = currentSessionSearch.advancedSearchId;
  const {data: searchStatusResponse, refetch: refetchSearchStatus} =
    useSearchStatus(currentSessionSearch.advancedSearchId ?? 0);
  const searchStatusRef = useRef(searchStatusResponse);
  const shouldPollStatusRef = useRef(shouldPollStatus);
  shouldPollStatusRef.current = shouldPollStatus;
  const hasSearchStatusResponse = searchStatusResponse != null;

  useEffect(() => {
    if (searchStatusResponse) {
      searchStatusRef.current = searchStatusResponse;
    }
  }, [searchStatusResponse]);

  const {mutateAsync: sendNotification} = useAddNotification();
  const [selectedIndexData, setSelectedIndexData] = useState<
    IIndexDropdownResponse | undefined
  >();

  const {mutateAsync: runsearch, isPending: isRunSearchLoading} = useRunSearch(
    undefined,
    (err) => {
      handleSearchFailure();
      console.error('Search failed:', err.message);
    }
  );

  const {mutateAsync: addSearchToQueueMutate, isPending: isAddToQueueLoading} =
    useAddSearchToQueue(undefined, (err) => {
      handleSearchFailure();
      console.error('Add search to queue failed:', err.message);
    });

  const {mutateAsync: loadsearchResult} = useReloadSearchResult();
  const {mutate: getSearchUniqueDocIds} = useGetSearchUniqueDocIds(
    (data) => {
      const limit = parseSavedSearchLimit(data);
      savedSearchLimitRef.current = limit;
      setSavedSearchLimit(limit);
    },
    (err) => {
      console.error('[session-search] /view/searches failed', err);
      savedSearchLimitRef.current = -1;
      setSavedSearchLimit(-1);
    }
  );

  /** Pure UI check before any Save/Search API. Returns false to block the action. */
  const ensureSavedSearchLimit = useCallback(
    (session: SessionSearch): boolean => {
      const savedSearchLimit = savedSearchLimitRef.current;

      // Limit not configured / not loaded — skip UI validation
      if (savedSearchLimit === -1) {
        return true;
      }

      if (isSavedSearchLimitExceeded(session, savedSearchLimit)) {
        setReadyForSearch(false);
        showSearchErrorToast(
          t('sessionSearch.errors.savedSearchLimitExceeded', {
            limit: savedSearchLimit,
          })
        );
        return false;
      }

      return true;
    },
    [showSearchErrorToast]
  );

  useEffect(() => {
    if (
      !hasInitializedFromRedux.current ||
      !sessionSearches.length ||
      currentSessionSearch.searchIsInProgress
    ) {
      return;
    }

    if (lastProjectContextKey.current === null) {
      lastProjectContextKey.current = projectContextKey;
    }

    if (lastProjectContextKey.current !== projectContextKey) {
      lastProjectContextKey.current = projectContextKey;

      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;

      setCanDiscardItem(false);
      setCurrentSessionSearchBackup(null);
      setCurrentSearchResultRequest(null);
      setShouldPollStatus(false);
      setShouldPollResults(false);
      setSearchIsInProgress(false);
      setUniqueDocIdCount(0);

      dispatch(ClearShoppingCart());
      dispatch(PersistSearches([]));
      sessionStorage.removeItem('presistence-search');

      setSessionSearches([newSearch]);
      setDisplaySearches([newSearch]);
      setCurrentSessionSearch(newSearch);

      return;
    }

    sessionStorage.setItem(
      'presistence-search',
      JSON.stringify({
        searches: sessionSearches,
        currentId: currentSessionSearch.id,
        projectId,
        userRoleId,
        userSecurityGroupId: userSecurityGroup,
      })
    );
  }, [
    projectContextKey,
    currentSessionSearch,
    sessionSearches,
    projectId,
    userRoleId,
    userSecurityGroup,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      hasHandledBackNavigation.current = false;
    };
  }, [location.pathname]);

  // STEP 1: Handle navigation state ONCE on mount
  useEffect(() => {
    const loadedSearch = location.state?.loadedSearch as
      | SessionSearch
      | undefined;
    const loadedSearches = location.state?.loadedSearches as
      | SessionSearch[]
      | undefined;
    if ((loadedSearch || loadedSearches) && !hasLoadedFromNavigation.current) {
      hasLoadedFromNavigation.current = true;
      hasInitializedFromRedux.current = true;
      let searchesToLoad: SessionSearch[] = [];
      if (loadedSearches && loadedSearches.length > 0) {
        searchesToLoad = loadedSearches;
      } else if (loadedSearch) {
        searchesToLoad = [loadedSearch];
      }
      const selectedSearchId =
        (searchesToLoad.find((s) => s.isSelected) || searchesToLoad[0])?.id ??
        '';
      searchesToLoad = searchesToLoad.map((search) => ({
        ...search,
        isSelected: search.id === selectedSearchId,
      }));
      let persistedSearches: SessionSearch[] = [];

      try {
        persistedSearches =
          JSON.parse(sessionStorage.getItem('presistence-search') || '{}')
            ?.searches ?? [];
      } catch {
        persistedSearches = [];
      }

      setSessionSearches((prevSearches) => {
        const mergedSearches = [
          ...(persistedSearches.length
            ? persistedSearches
            : prevSearches?.length
              ? prevSearches
              : (persistentSearches ?? [])),
        ];

        searchesToLoad.forEach((loadedSearch) => {
          const index = mergedSearches.findIndex(
            (search) =>
              search.id === loadedSearch.id ||
              (!!search.advancedSearchId &&
                !!loadedSearch.advancedSearchId &&
                search.advancedSearchId === loadedSearch.advancedSearchId)
          );

          index >= 0
            ? // eslint-disable-next-line security/detect-object-injection
              (mergedSearches[index] = {
                // eslint-disable-next-line security/detect-object-injection
                ...mergedSearches[index],
                ...loadedSearch,
              })
            : mergedSearches.unshift(loadedSearch);
        });

        return mergedSearches.map((search) => ({
          ...search,
          isSelected: search.id === selectedSearchId,
        }));
      });
      const selectedSearch =
        searchesToLoad.find((s) => s.isSelected) || searchesToLoad[0];
      if (selectedSearch) {
        selectionApplied.current = true;
        setCurrentSessionSearch(selectedSearch);
      }
      searchesToLoad.forEach((search) => {
        dispatch(AddUnsavedSearch(search));
      });
    }
  }, [dispatch]);

  // STEP 2: Initialize from Redux when it updates
  useEffect(() => {
    if (hasInitializedFromRedux.current) {
      return;
    }
    const savedPresistance = sessionStorage.getItem('presistence-search');
    hasInitializedFromRedux.current = true;

    let persistedSearches: SessionSearch[] = [];
    let persistedProjectId: string | number | null = null;
    let persistedUserRoleId: string | number | null = null;
    let persistedSecurityGroupId: string | number | null = null;
    let persistedCurrentId: string | null = null;

    if (savedPresistance) {
      try {
        const parsed = JSON.parse(savedPresistance);
        persistedSearches = (parsed.searches as SessionSearch[]) ?? [];
        persistedProjectId = parsed.projectId ?? null;
        persistedUserRoleId = parsed.userRoleId ?? null;
        persistedSecurityGroupId = parsed.userSecurityGroupId ?? null;
        persistedCurrentId = parsed.currentId ?? null;
      } catch {
        persistedSearches = [];
      }
    }

    // Start from Redux persisted searches
    const localSearches: SessionSearch[] = JSON.parse(
      JSON.stringify(persistentSearches ?? [])
    );
    const hasContextChanged =
      (persistedProjectId !== null && persistedProjectId !== projectId) ||
      (persistedUserRoleId !== null && persistedUserRoleId !== userRoleId) ||
      (persistedSecurityGroupId !== null &&
        persistedSecurityGroupId !== userSecurityGroup);

    if (hasContextChanged) {
      persistedSearches = [];
      dispatch(ClearShoppingCart());
      dispatch(PersistSearches([]));
      sessionStorage.removeItem('presistence-search');
      persistedCurrentId = null;
      localSearches.length = 0;
    }
    //merge persistedSearches from sessionStorage into localSearches
    if (persistedSearches.length > 0) {
      for (const p of persistedSearches) {
        const idx = localSearches.findIndex((s) => s.id === p.id);
        if (idx >= 0) {
          // merge, giving precedence to the latest persisted version
          // eslint-disable-next-line security/detect-object-injection
          localSearches[idx] = {
            // eslint-disable-next-line security/detect-object-injection
            ...localSearches[idx],
            ...p,
          };
        } else {
          localSearches.push(p);
        }
      }
    }
    if (localSearches.length === 0) {
      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;
      localSearches.push(newSearch);
    }
    for (const s of localSearches) {
      s.isSelected = false;
    }

    let selected: SessionSearch | undefined;
    if (persistedCurrentId) {
      selected = localSearches.find((s) => s.id === persistedCurrentId);
    }
    if (!selected) {
      selected = localSearches.find((s) => s.isSelected) || localSearches[0];
    }
    if (selected) {
      selected.isSelected = true;
      setCurrentSessionSearch(selected);
      selectionApplied.current = true;
    }

    setSessionSearches(localSearches);

    if (savedPresistance) {
      sessionStorage.removeItem('presistence-search');
    }
  }, [persistentSearches]);

  useEffect(() => {
    // Remove Searched if User updates project from <Dropdown>. Reaches DocExplorer -> Again comes back to previous project . Now navigates to Session Search
    return () => {
      const savedPresistance = sessionStorage.getItem('presistence-search');

      let persistedProjectId: string | number | null = null;
      let persistedUserRoleId: string | number | null = null;
      let persistedSecurityGroupId: string | number | null = null;

      if (savedPresistance) {
        try {
          const parsed = JSON.parse(savedPresistance);
          persistedProjectId = parsed.projectId ?? null;
          persistedUserRoleId = parsed.userRoleId ?? null;
          persistedSecurityGroupId = parsed.userSecurityGroupId ?? null;
        } catch {
          console.group();
        }
      }
      const hasContextChanged =
        (persistedProjectId !== null && persistedProjectId !== projectId) ||
        (persistedUserRoleId !== null && persistedUserRoleId !== userRoleId) ||
        (persistedSecurityGroupId !== null &&
          persistedSecurityGroupId !== userSecurityGroup);

      if (hasContextChanged) {
        dispatch(ClearShoppingCart());
        dispatch(PersistSearches([]));
        sessionStorage.removeItem('presistence-search');
      }
    };
  }, [location.pathname, projectId]);

  useEffect(() => {
    const searchId = currentSessionSearch.advancedSearchId ?? 0;
    const searchName =
      currentSessionSearch.name && currentSessionSearch.name.trim()
        ? currentSessionSearch.name
        : 'SearchName';

    getSearchUniqueDocIds({
      searchId,
      redirectToSearch: 'Advanced',
      searchName: searchName,
    });
  }, []);

  // STEP 3: Update UI properties
  useEffect(() => {
    if (!hasInitializedFromRedux.current) {
      return;
    }
    const restoredSearches = [...sessionSearches];
    const pinCount = restoredSearches.filter((i) => i.isPinned === true).length;
    const lessThanNinePinnedSearches = pinCount < 9;
    setCanPinItem(lessThanNinePinnedSearches);
    let searchesCount = restoredSearches.length;
    while (searchesCount > 10) {
      const unpinnedIndex = restoredSearches.findLastIndex(
        (i) => i.isPinned !== true
      );
      restoredSearches.splice(unpinnedIndex, 1);
      searchesCount = restoredSearches.length;
    }
    let ordinal = restoredSearches.length;
    for (const search of restoredSearches) {
      search.ordinal = ordinal;
      search.color = SessionSearchColors[ordinal - 1];
      if (!search.isSaved) {
        search.name = t('sessionSearch.listItems.name', {index: ordinal});
      }
      ordinal--;
    }

    if (!hasHandledBackNavigation.current && !selectionApplied.current) {
      for (const sessionSearch of restoredSearches) {
        sessionSearch.isSelected = false;
      }

      const currentSearch = restoredSearches.find(
        (value) => value.id === currentSessionSearch.id
      );

      if (currentSearch) {
        currentSearch.isSelected = true;
      } else {
        const newCurrentSearch = restoredSearches[0];
        newCurrentSearch.isSelected = true;
        setCurrentSessionSearch(newCurrentSearch);
      }
    }

    setDisplaySearches(restoredSearches);
    if (!canDiscardItem) {
      const savedAndExecutedSearches = restoredSearches.filter(
        (i) => i.isSaved && isExecuted(i)
      );
      const createSignature = (searches: SessionSearch[]) => {
        return searches
          .map(
            (s) =>
              `${s.id}:${s.isSaved}:${s.lastRuntime?.getTime?.() ?? 'none'}`
          )
          .sort((a, b) => a.localeCompare(b))
          .join('\n');
      };
      const currentSig = createSignature(persistentSearches || []);
      const newSig = createSignature(savedAndExecutedSearches);
      if (currentSig !== newSig) {
        const deepCopy = structuredClone(savedAndExecutedSearches);
        dispatch(PersistSearches(deepCopy));
      }
    }
  }, [sessionSearches, currentSessionSearch.id, canDiscardItem]);

  const searchRef = useRef(currentSessionSearch);

  useEffect(() => {
    searchRef.current = currentSessionSearch;
  }, [currentSessionSearch]);

  useEffect(() => {
    if (
      !hasInitializedFromRedux.current ||
      !sessionSearches.length ||
      currentSessionSearch.searchIsInProgress
    ) {
      return;
    }

    sessionStorage.setItem(
      'presistence-search',
      JSON.stringify({
        searches: sessionSearches,
        currentId: currentSessionSearch.id,
        projectId,
        userRoleId,
        userSecurityGroupId: userSecurityGroup,
      })
    );
  }, [
    currentSessionSearch,
    sessionSearches,
    projectId,
    userRoleId,
    userSecurityGroup,
  ]);

  const resetSearchState = () => {
    const updated = structuredClone(currentSessionSearch);
    updated.searchIsInProgress = false;
    updated.advancedSearchId = 0;
    updated.lastRuntime = undefined;

    setCurrentSessionSearch(updated);
    updateItemState(updated);
  };

  const handleSearchFailure = () => {
    resetSearchState();
    showSearchErrorToast(t('sessionSearch.unexpectedError'));
  };

  useEffect(() => {
    // Ignore all events if the current search is not running
    if (
      !searchRef.current.searchIsInProgress ||
      !searchRef.current.searchNotificationId
    )
      return;

    const reloadSearchResult = async (event: SignalREvent) => {
      if (!event) return;

      const outer = event.payload;
      const inner = outer?.payload ?? outer;
      const eventType = outer?.type ?? event.type;

      const isSuccess = eventType === SEARCHSUCCESS_SIGNALR_TYPE;
      const isFailed = eventType === SEARCHFAILED_SIGNALR_TYPE;
      if (!isSuccess && !isFailed) return;

      const searchId = inner?.searchID;
      if (!searchId) return;

      // Only process events for the active search
      if (searchRef.current.advancedSearchId !== searchId) return;

      // FAILED event
      if (isFailed) {
        handleSearchFailure();
        return;
      }
      if (
        !searchRef.current.searchIsInProgress ||
        !searchRef.current.searchNotificationId
      )
        return;
      // SUCCESS event → load result
      const request = toSearchResultRequest(searchRef.current);
      const data = await loadsearchResult(request);

      const anyError =
        data.pureHitStatus === 'ERROR' ||
        data.emailThreadStatus === 'ERROR' ||
        data.familyMemberStatus === 'ERROR' ||
        data.nearDuplicateStatus === 'ERROR';

      if (anyError) {
        handleSearchFailure();
        return;
      }

      const allComplete =
        data.pureHitStatus === 'COMPLETE' &&
        data.emailThreadStatus === 'COMPLETE' &&
        data.familyMemberStatus === 'COMPLETE' &&
        data.nearDuplicateStatus === 'COMPLETE';

      if (!allComplete) {
        // Update partial results
        const updated = structuredClone(searchRef.current);
        updated.searchResult = toSearchResult(data);
        setCurrentSessionSearch(updated);
        updateItemState(updated);
        searchRef.current = updated;

        return;
      }

      // Update current search
      const updated = structuredClone(searchRef.current);
      updated.searchIsInProgress = false;
      updated.searchResult = toSearchResult(data);

      setCurrentSessionSearch(updated);
      updateItemState(updated);
      searchRef.current = updated;
    };

    // Find the event that belongs to the current search
    const eventForCurrentSearch = signalREvents.find((evt) => {
      const type = evt?.payload?.type;
      const isRelevantType =
        type === SEARCHSUCCESS_SIGNALR_TYPE ||
        type === SEARCHFAILED_SIGNALR_TYPE;

      if (!isRelevantType) return false;

      const eventSearchId =
        evt.payload?.payload?.searchID ?? evt.payload?.searchID;

      return eventSearchId === searchRef.current.advancedSearchId;
    });

    if (eventForCurrentSearch) {
      reloadSearchResult(eventForCurrentSearch);
    }
  }, [
    signalREvents,
    currentSessionSearch.advancedSearchId,
    currentSessionSearch.searchNotificationId,
  ]);

  useEffect(() => {
    const persistentSearchesCount = displaySearches.filter(
      (i) => i.isSaved && isExecuted(i)
    ).length;
    const hasLessThanMaxSearches =
      displaySearches.length < 11 && hasAnyCriteriaAdded(currentSessionSearch);
    if (persistentSearchesCount === 10) {
      setCanAddItem(true);
    } else if (hasLessThanMaxSearches) {
      setCanAddItem(true);
    } else {
      setCanAddItem(false);
    }
  }, [currentSessionSearch]);

  useEffect(() => {
    if (canContinueWithSearch) {
      const doValidation = async () => {
        try {
          const metadataCriteria = currentSessionSearch.query?.find(
            (q) => q.type === QueryType.ContentAndMetadata
          );

          if (metadataCriteria) {
            const criteriaStr = getCriteriaValues(metadataCriteria);

            if (criteriaStr) {
              const result = await validateSearchCriteria.mutateAsync({
                searchCriteria: criteriaStr,
                customIndexID:
                  searchRef.current.indexId ??
                  currentSessionSearch.indexId ??
                  0,
              });
              if (!result) {
                setSearchIsInProgress(false);
                showSearchErrorToast(
                  t('sessionSearch.searchValidationErrorToast'),
                  ToastType.Warning
                );
              } else {
                setReadyForSearch(true);
              }
            } else {
              setSearchIsInProgress(false);
            }
          } else {
            setReadyForSearch(true);
          }
        } catch (err: any) {
          setSearchIsInProgress(false);
          const status = err?.response?.status || err?.status;
          if (status === 500) {
            showSearchErrorToast(
              t('sessionSearch.searchErrors.searchValidationError')
            );
            throw new Error(
              t('sessionSearch.errors.analyticInternalServerError')
            );
          }
          console.error('Caught error while search:', err.message);
        }
      };
      setcanContinueWithSearch(false);
      void doValidation();
    }
  }, [canContinueWithSearch]);

  //runsearch → nested modal decision → add-to-search-queue on proceed
  useEffect(() => {
    if (!readyForSearch) return;
    const doSearch = async () => {
      try {
        setReadyForSearch(false);
        queueRequestRef.current = null;
        const request = createSearchRequest(searchRef.current);
        const result = await runsearch(request);
        const flow = resolveSessionRunSearchFlow(result, request);

        if (flow.messages.length > 0) {
          showSearchErrorToast(flow.messages);
        }

        if (flow.outcome === 'error') {
          setSearchIsInProgress(false);
          return;
        }

        if (flow.outcome === 'proceed') {
          queueRequestRef.current = flow.queueRequest;
          setSearchIsInProgress(false);
          void handleAddSearchToQueue();
          return;
        }

        queueRequestRef.current = flow.queueRequest;

        if (flow.outcome === 'large') {
          setRows(flow.rows);
          setOpenNestedSearchModal(true);
        } else {
          setNestedChildModal(true);
        }
        setSearchIsInProgress(false);
      } catch (err: any) {
        setSearchIsInProgress(false);
        console.error('Caught error while search:', err.message);
      }
    };
    setSearchIsInProgress(true);
    void doSearch();
  }, [readyForSearch]);

  const handleAddSearchToQueue = useCallback(async () => {
    const queueRequest = queueRequestRef.current;
    if (!queueRequest) {
      showSearchErrorToast(t('savedSearch.executeNestedSearch.executeError'));
      setOpenNestedSearchModal(false);
      setNestedChildModal(false);
      return;
    }

    try {
      setOpenNestedSearchModal(false);
      setNestedChildModal(false);
      setSearchIsInProgress(true);
      const searchDetails = createSearchRequest(searchRef.current);
      const data = await addSearchToQueueMutate({
        ...queueRequest,
        searchDetails: {
          ...searchDetails,
          searchSetGUID: queueRequest.setId,
          advSearchID:
            queueRequest.searchDetails?.advSearchID ??
            searchDetails.advSearchID,
        },
      });
      queueRequestRef.current = null;

      const updated = structuredClone(searchRef.current);
      updated.searchIsInProgress = true;
      updated.searchNotificationId = 0;
      updated.searchResult = createEmptySearchResult();
      updated.searchResult.conceptualStatus = 'INPROGRESS';
      updated.searchResult.pureHitStatus = 'INPROGRESS';
      updated.searchResult.emailThreadStatus = 'INPROGRESS';
      updated.searchResult.familyMemberStatus = 'INPROGRESS';
      updated.searchResult.nearDuplicateStatus = 'INPROGRESS';

      if (data.sessionSearchList?.length > 0) {
        const searchResultRequest = createSearchResultRequest(data);
        updated.advancedSearchId = searchResultRequest.searchId;
        setCurrentSearchResultRequest(searchResultRequest);
      }

      setCurrentSessionSearch(updated);
      updateItemState(updated);
      setShouldPollResults(false);
      setShowSearchWaitModal(false);
      setSearchWaitPhase('initial');
      searchWaitStartedAtRef.current = null;
      setShouldPollStatus(true);
    } catch (err: any) {
      setSearchIsInProgress(false);
      console.error('Caught error while adding search to queue:', err.message);
    }
  }, [addSearchToQueueMutate, showSearchErrorToast]);

  useEffect(() => {
    if (!shouldPollStatus || !searchId || !hasSearchStatusResponse) return;

    let cancelled = false;

    const pollStatus = async () => {
      try {
        const result: any = await refetchSearchStatus();
        if (cancelled) return;
        if (result?.error) {
          const err = result.error as Error;
          console.log('Error fetching search status:', err);

          setShouldPollStatus(false);
          setShouldPollResults(false);
          setSearchIsInProgress(false);
          handleSearchFailure();
          return;
        }

        const status = result?.data?.searchStatus as number | undefined;
        if (typeof status === 'number') {
          searchStatusRef.current = {searchStatus: status};
        }

        if (isSearchStatusReady(status)) {
          setShowSearchWaitModal(false);
          setShouldPollStatus(false);
          setShouldPollResults(true);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.log('Error fetching search status:', err);
        setShouldPollStatus(false);
        setShouldPollResults(false);
        setSearchIsInProgress(false);
        handleSearchFailure();
      }
    };

    void pollStatus();
    const intervalId = setInterval(pollStatus, SEARCH_STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [shouldPollStatus, searchId, hasSearchStatusResponse]);

  useEffect(() => {
    if (!shouldPollStatus || !searchStatusResponse) return;

    if (isSearchStatusReady(searchStatusResponse.searchStatus)) {
      setShowSearchWaitModal(false);
      setShouldPollStatus(false);
      setShouldPollResults(true);
    }
  }, [searchStatusResponse, shouldPollStatus]);

  useEffect(() => {
    if (!shouldPollStatus || !hasSearchStatusResponse) return;
    if (isSearchStatusReady(searchStatusRef.current?.searchStatus)) {
      return;
    }

    if (searchWaitStartedAtRef.current == null) {
      searchWaitStartedAtRef.current = Date.now();
    }

    const elapsedMs = Date.now() - searchWaitStartedAtRef.current;
    const remainingMs = Math.max(
      0,
      SEARCH_WAIT_DEADLINE_MS[searchWaitPhase] - elapsedMs
    );

    const timeoutId = setTimeout(() => {
      if (!shouldPollStatusRef.current) {
        return;
      }

      if (isSearchStatusReady(searchStatusRef.current?.searchStatus)) {
        setShowSearchWaitModal(false);
        setShouldPollStatus(false);
        setShouldPollResults(true);
        return;
      }

      if (searchWaitPhase === 'final') {
        void handleNotifyAllRef.current();
        return;
      }

      setShouldPollStatus(false);
      setShouldPollResults(false);
      setSearchIsInProgress(false);
      setShowSearchWaitModal(true);
    }, remainingMs);

    return () => clearTimeout(timeoutId);
  }, [shouldPollStatus, hasSearchStatusResponse, searchWaitPhase]);

  const handleWaitForAllResults = () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(true);
    setSearchWaitPhase((prev) => getNextSearchWaitPhase(prev));

    if (isSearchStatusReady(searchStatusRef.current?.searchStatus)) {
      setShouldPollStatus(false);
      setShouldPollResults(true);
      return;
    }

    setShouldPollResults(false);
    setShouldPollStatus(true);
  };

  const handleNotifyPureHits = async () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(false);
    setShouldPollStatus(false);
    setShouldPollResults(false);
    setSearchWaitPhase('initial');
    searchWaitStartedAtRef.current = null;
    const sessionSearch =
      currentSearchResultRequest?.sessionSearchList?.[0] ??
      ({} as SessionSearchItem);

    const notificationId = await sendNotification({
      searchId: currentSessionSearch.advancedSearchId ?? 0,
      notificationType: 'ADD',
      searchNotifyType: 'PH',
      sessionSearch,
    });
    const updated = structuredClone(currentSessionSearch);
    updated.searchNotificationId = notificationId;

    setCurrentSessionSearch(updated);
    updateItemState(updated);
    searchRef.current = updated;
    showSearchErrorToast(
      t('sessionSearch.searchBackgroundToast', {taskId: notificationId}),
      ToastType.Success
    );
  };

  const handleNotifyAll = async () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(false);
    setShouldPollStatus(false);
    setShouldPollResults(false);
    setSearchWaitPhase('initial');
    searchWaitStartedAtRef.current = null;
    const sessionSearch =
      currentSearchResultRequest?.sessionSearchList?.[0] ??
      ({} as SessionSearchItem);

    const notificationId = await sendNotification({
      searchId: currentSessionSearch.advancedSearchId ?? 0,
      notificationType: 'ADD',
      searchNotifyType: 'ALL',
      sessionSearch,
    });

    const updated = structuredClone(currentSessionSearch);
    updated.searchNotificationId = notificationId;
    setCurrentSessionSearch(updated);
    updateItemState(updated);
    searchRef.current = updated;
    showSearchErrorToast(
      t('sessionSearch.searchBackgroundToast', {taskId: notificationId}),
      ToastType.Success
    );
  };
  handleNotifyAllRef.current = handleNotifyAll;

  //loadsearchResult — only after search status is 4 or 1
  useEffect(() => {
    if (!shouldPollResults || !currentSearchResultRequest) return;

    setSearchIsInProgress(true);

    let cancelled = false;
    let completed = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const pollResults = async () => {
      if (cancelled || completed) return;

      try {
        const data = await loadsearchResult(currentSearchResultRequest);
        if (cancelled || completed) return;

        if (
          data.pureHitStatus === 'ERROR' ||
          data.emailThreadStatus === 'ERROR' ||
          data.familyMemberStatus === 'ERROR' ||
          data.nearDuplicateStatus === 'ERROR'
        ) {
          completed = true;
          setShouldPollResults(false);
          setSearchIsInProgress(false);
          currentSessionSearch.advancedSearchId = 0;
          currentSessionSearch.lastRuntime = undefined;

          return;
        }

        const done =
          data.pureHitStatus === 'COMPLETE' &&
          data.emailThreadStatus === 'COMPLETE' &&
          data.familyMemberStatus === 'COMPLETE' &&
          data.nearDuplicateStatus === 'COMPLETE';
        if (done) {
          completed = true;
          setShouldPollResults(false);
          setSearchIsInProgress(false);
          currentSessionSearch.searchIsInProgress = false;

          currentSessionSearch.advancedSearchId =
            currentSearchResultRequest.searchId;
          currentSessionSearch.lastRuntime = new Date();
          currentSessionSearch.searchResult = toSearchResult(data);
          updateItemState(currentSessionSearch);
          setCurrentSessionSearch({...currentSessionSearch});
        }
      } catch {
        if (cancelled || completed) return;
        completed = true;
        handleSearchFailure();
        setShouldPollResults(false);
        setSearchIsInProgress(false);
      }
    };

    void pollResults();
    intervalId = setInterval(pollResults, SEARCH_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [shouldPollResults, currentSearchResultRequest]);

  const extractErrorCode = (msgs: string[]): string => {
    if (!msgs || msgs.length === 0) return '';
    const firstMsg = msgs[0];
    const parts = firstMsg.split(':');
    return parts[0].trim();
  };

  const extractErrorMessage = (msgs: string[]): string => {
    if (!msgs || msgs.length === 0) return '';
    const formatted = msgs.map((msg, index) => {
      const code = msg.split(':')[0].trim();
      const translated = getT(code);
      return `${index + 1}. ${translated}`;
    });
    return formatted.join(' </br></br> ');
  };

  const getT = (id: string) => {
    return t(`sessionSearch.searchErrors.${id}`);
  };

  const getErrorCode = (input: string): string => {
    const numericPart = input.replace(/^\D+/, '');
    return numericPart;
  };

  const removeAfterDot = (input: string): string => {
    const index = input.indexOf('.');
    if (index === -1) {
      return input;
    }
    return input.substring(0, index).trim();
  };

  const containsHtml = (input: string): boolean => {
    return /<\/?[a-z][^>\r\n]{0,1000}>/i.test(input);
  };

  // validatesearch
  const validateSearch = useCallback(
    async (session: SessionSearch): Promise<void> => {
      const isEmptyCriteria = (val?: string | null) =>
        !val || val.trim().length === 0;

      // --- Step 1: Analytics ---
      try {
        const analyticsCriteria = session.query?.find(
          (q) => q.type === QueryType.Analytics
        );

        if (analyticsCriteria) {
          const criteriaStr = getCriteriaValues(analyticsCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            showSearchErrorToast(t('sessionSearch.searchValidationErrorToast'));
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortAnalyticValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateAnalytic.mutateAsync(criteriaStr);
            if (!result.searchStatus) {
              setShowAnalyticWorProductValidation(true);
              const rawError = getT(getErrorCode(result.error));
              const hasHtml = containsHtml(rawError);
              setErrorStringContainsHtml(hasHtml);
              const finalError = result.error?.startsWith('E')
                ? removeAfterDot(rawError)
                : rawError;
              setCriticalValidationError(finalError);
              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortAnalyticValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          showSearchErrorToast(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          throw new Error(
            t('sessionSearch.errors.analyticInternalServerError')
          );
        } else {
          handleSearchFailure();
        }
        throw new Error(
          t('sessionSearch.errors.analyticValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 2: WorkProduct ---
      try {
        const workProductCriteria = session.query?.find(
          (q) => q.type === QueryType.WorkProduct
        );

        if (workProductCriteria) {
          const criteriaStr = getCriteriaValues(workProductCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            showSearchErrorToast(t('sessionSearch.searchValidationErrorToast'));
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortWorkProductValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateWorkProduct.mutateAsync(criteriaStr);
            if (!result.searchStatus) {
              setShowAnalyticWorProductValidation(true);

              const rawError = getT(getErrorCode(result.error));
              const hasHtml = containsHtml(rawError);

              setErrorStringContainsHtml(hasHtml);

              const finalError = result?.error?.startsWith('E')
                ? removeAfterDot(rawError)
                : rawError;

              setCriticalValidationError(finalError);

              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortWorkProductValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          showSearchErrorToast(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          throw new Error(
            t('sessionSearch.errors.workProductInternalServerError')
          );
        } else {
          handleSearchFailure();
        }
        throw new Error(
          t('sessionSearch.errors.workProductValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 3: Content & Metadata ---
      try {
        const contentMetaCriteria = session.query?.find(
          (q) => q.type === QueryType.ContentAndMetadata
        );

        if (contentMetaCriteria) {
          const criteriaStr = buildCriteriaString(contentMetaCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            showSearchErrorToast(t('sessionSearch.searchValidationErrorToast'));
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortContentMetadataValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateContentMeta.mutateAsync({
              searchCriteria: criteriaStr,
              customIndexID: session.indexId ?? 0,
            });
            if (!result.status) {
              let rawError = '';
              let hasHtml = true;
              if (result.msgs && result.msgs.length > 1) {
                rawError = extractErrorMessage(result.msgs);
              } else {
                rawError = getT(extractErrorCode(result.msgs));
                hasHtml = containsHtml(rawError);
              }
              setErrorStringContainsHtml(hasHtml);
              if (result.canContinue) {
                setContentMDValidationError(rawError);
                setShowContentMDValidation(true);
              } else {
                setShowAnalyticWorProductValidation(true);
                setCriticalValidationError(rawError);
              }
              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortContentMetadataValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          showSearchErrorToast(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          throw new Error(
            t('sessionSearch.errors.contentMetadataInternalServerError')
          );
        } else {
          resetSearchState();
        }
        throw new Error(
          t('sessionSearch.errors.contentMetadataValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 4: conceptual ---

      const conceptualCriteria = session.query?.find(
        (q) => q.type === QueryType.Conceptual
      );

      if (conceptualCriteria) {
        const criteriaStr = buildCriteriaString(conceptualCriteria);
        if (isEmptyCriteria(criteriaStr)) {
          setReadyForSearch(false);
          showSearchErrorToast(t('sessionSearch.searchValidationErrorToast'));
          throw new Error(t('sessionSearch.errors.conceptualValidationFailed'));
        }
      }
    },
    [
      validateAnalytic,
      validateWorkProduct,
      validateContentMeta,
      validateSearchCriteria,
    ]
  );
  const updateItemState = (item: SessionSearch) => {
    setSessionSearches((prev) =>
      prev.some((value) => value.id === item.id)
        ? prev.map((value) => (value.id === item.id ? item : value))
        : [item, ...prev.map((value) => ({...value, isSelected: false}))]
    );
  };

  const handleSearchModifiersChanged = (item: SessionSearch) => {
    const clonedItem = structuredClone(item);
    updateItemState(clonedItem);
    setCurrentSessionSearch(clonedItem);
  };

  const handleItemSelected = (_index: number, item: SessionSearch) => {
    if (currentSessionSearch.id === item.id) {
      return;
    }
    if (canDiscardItem && currentSessionSearchBackup) {
      setCanDiscardItem(false);
      updateItemState(currentSessionSearchBackup);
      setCurrentSessionSearchBackup(null);
    }
    for (const sessionSearch of sessionSearches) {
      sessionSearch.isSelected = false;
    }
    item.isSelected = true;
    updateItemState(item);
    const clonedItem = structuredClone(item);
    setCurrentSessionSearch(clonedItem);
  };

  const handleItemPinned = (_index: number, item: SessionSearch) => {
    updateItemState(item);
  };

  const handleItemUnpinned = (_index: number, item: SessionSearch) => {
    updateItemState(item);
  };

  const handleItemDeleted = (_index: number, item: SessionSearch) => {
    let filteredSearches = sessionSearches.filter(
      (value) => value.id !== item.id
    );
    if (filteredSearches.length == 0) {
      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;
      filteredSearches = [newSearch];
    }
    setSessionSearches(filteredSearches);
  };

  const handleItemAdded = () => {
    if (!canAddItem) {
      console.error(t('sessionSearch.errors.maxSearchLimitReached'));
      return;
    }

    const newSearch = new DefaultSessionSearch();
    newSearch.isSelected = true;
    for (const sessionSearch of sessionSearches) {
      sessionSearch.isSelected = false;
    }
    const updatedSearches = [newSearch, ...sessionSearches];
    setSessionSearches(updatedSearches);
    setCurrentSessionSearch(newSearch);
  };

  const handleItemDiscarded = () => {
    setCanDiscardItem(false);
    if (currentSessionSearchBackup) {
      if (currentSessionSearch.id === currentSessionSearchBackup.id) {
        updateItemState(currentSessionSearchBackup);
      }
      setCurrentSessionSearch({...currentSessionSearchBackup});
      setCurrentSessionSearchBackup(null);
    }
  };

  const handleCopy = (_item: SessionSearch) => {
    if (sessionSearches.length >= 10) {
      return;
    }
    const copiedQuery = _item.query ? structuredClone(_item.query) : [];

    const queryWithNewIds = copiedQuery.map((q: Query) => ({
      ...q,
      value: q.value.map((c: Criteria) => ({
        ...c,
        id: uuidv4(),
      })),
    }));
    const copiedSearch: SessionSearch = {
      id: uuidv4(),
      name: '',
      familyMemberDocuments: _item.familyMemberDocuments,
      threadedEmailDocuments: _item.threadedEmailDocuments,
      nearDuplicateDocuments: _item.nearDuplicateDocuments,
      regexOnWorkProductAttributes: _item.regexOnWorkProductAttributes,
      isSelected: true,
      isPinned: false,
      isSaved: false,
      lastRuntime: undefined,
      advancedSearchId: undefined,
      searchResult: undefined,
      ordinal: undefined,
      color: undefined,
      query: queryWithNewIds,
      indexId: _item?.indexId,
    };
    const updatedSearches = sessionSearches.map((search) => ({
      ...search,
      isSelected: false,
    }));
    const newSearches = [copiedSearch, ...updatedSearches];
    setSessionSearches(newSearches);
    setCurrentSessionSearch(copiedSearch);
  };

  const handleSave = (
    item: SessionSearch,
    selectedIndexDropdown: IIndexDropdownResponse | undefined
  ) => {
    setSelectedIndexData(selectedIndexDropdown);
    if (!ensureSavedSearchLimit(searchRef.current ?? item)) {
      return;
    }
    setIsSaveDrawerOpen(true);
  };

  const handleModify = (item: SessionSearch) => {
    setCanDiscardItem(true);

    const queryWithNewIds = (item.query ?? []).map((q: Query) => ({
      ...q,
      value: q.value.map((c: Criteria) => ({
        ...c,
        id: uuidv4(),
      })),
    }));

    const updated: SessionSearch = {
      ...item,
      id: uuidv4(),
      isSelected: true,
      isPinned: false,
      isSaved: false,
      lastRuntime: undefined,
      advancedSearchId: undefined,
      searchResult: undefined,
      ordinal: undefined,
      color: undefined,
      query: queryWithNewIds,
    };

    const clonedUpdated = structuredClone(updated);
    setCurrentSessionSearch(clonedUpdated);

    const backup = structuredClone(item);
    setCurrentSessionSearchBackup(backup);
  };

  const handleSearch = async (
    item: SessionSearch,
    selectedIndexDropdown?: IIndexDropdownResponse
  ) => {
    // UI-side only — must run before any validation / runsearch / queue API
    setSelectedIndexData(selectedIndexDropdown);
    const session: SessionSearch = {
      ...(searchRef.current ?? item),
      indexId: selectedIndexDropdown?.indexID ?? item.indexId ?? 0,
    };
    searchRef.current = session;
    setCurrentSessionSearch(session);
    updateItemState(session);

    if (!ensureSavedSearchLimit(session)) {
      return;
    }

    setSearchIsInProgress(true);
    setSessionSearchItem(session);
    setCanDiscardItem(false);
    queueRequestRef.current = null;
    await handleProceedToValidation(session);
  };

  const handleProceedToValidation = useCallback(
    async (sessionSearchItemData?: SessionSearch) => {
      try {
        await validateSearch(
          sessionSearchItemData ?? (sessionSearchItem as SessionSearch)
        );
        setcanContinueWithSearch(true);
      } catch (err: any) {
        setSearchIsInProgress(false);
        if (err instanceof SearchValidationAbortError) {
          return;
        }
        console.error(err);
      }
    },
    [sessionSearchItem, validateSearch]
  );

  const handleItemModified = (item: SessionSearch) => {
    const clonedItem = structuredClone(item);
    setSessionSearches((prev) =>
      prev.map((value) => {
        if (value.id === item.id) {
          return clonedItem;
        } else {
          return value;
        }
      })
    );
    setCurrentSessionSearch(clonedItem);
  };

  const handleIndexChange = (
    item: SessionSearch,
    selectedIndexDropdown: IIndexDropdownResponse
  ) => {
    handleItemModified(item);
    setUniqueDocIdCount(getIndexDocCount(selectedIndexDropdown));
  };

  const handleAddToShoppingCart = (type: ResultType): boolean => {
    if (!canPinItem) {
      setShowPinToast(true);
      return false;
    }
    const sessionSearchCopy = structuredClone(currentSessionSearch);
    if (sessionSearchCopy.searchResult) {
      switch (type) {
        case ResultType.PureHit:
          if (sessionSearchCopy.searchResult?.pureHit) {
            sessionSearchCopy.searchResult.pureHit.addedToShoppingCart = true;
          }
          break;
        case ResultType.FamilyMember:
          if (sessionSearchCopy.searchResult?.familyMember) {
            sessionSearchCopy.searchResult.familyMember.addedToShoppingCart = true;
          }
          break;
        case ResultType.EmailThread:
          if (sessionSearchCopy.searchResult?.emailThread) {
            sessionSearchCopy.searchResult.emailThread.addedToShoppingCart = true;
          }
          break;
        case ResultType.NearDupe:
          if (sessionSearchCopy.searchResult?.nearDuplicate) {
            sessionSearchCopy.searchResult.nearDuplicate.addedToShoppingCart = true;
          }
          break;
        case ResultType.Conceptual:
          if (sessionSearchCopy.searchResult)
            sessionSearchCopy.searchResult.ConceptualResultInShoppingCart = true;
          break;
        default:
          console.warn('Unknown ResultType:', type);
      }
    }
    sessionSearchCopy.isPinned = true;
    setCurrentSessionSearch(sessionSearchCopy);
    setSessionSearches((prev) =>
      prev.map((value) =>
        value.id === sessionSearchCopy.id ? sessionSearchCopy : value
      )
    );
    return true;
  };

  const handleResultRemoved = (id: string, type: ResultType) => {
    const updateSearch = (search: SessionSearch): SessionSearch => {
      if (search.id !== id || !search.searchResult) return search;
      const searchCopy = structuredClone(search);
      switch (type) {
        case ResultType.PureHit:
          if (searchCopy.searchResult?.pureHit) {
            searchCopy.searchResult.pureHit.addedToShoppingCart = false;
          }
          break;
        case ResultType.EmailThread:
          if (searchCopy.searchResult?.emailThread) {
            searchCopy.searchResult.emailThread.addedToShoppingCart = false;
          }
          break;
        case ResultType.FamilyMember:
          if (searchCopy.searchResult?.familyMember) {
            searchCopy.searchResult.familyMember.addedToShoppingCart = false;
          }
          break;
        case ResultType.NearDupe:
          if (searchCopy.searchResult?.nearDuplicate) {
            searchCopy.searchResult.nearDuplicate.addedToShoppingCart = false;
          }
          break;
        case ResultType.Conceptual:
          if (searchCopy.searchResult) {
            searchCopy.searchResult.ConceptualResultInShoppingCart = false;
          }
          break;
        default:
          console.warn('Unknown ResultType:', type);
      }
      const sr = searchCopy.searchResult;
      if (
        sr &&
        sr.pureHit?.addedToShoppingCart === false &&
        sr.emailThread?.addedToShoppingCart === false &&
        sr.familyMember?.addedToShoppingCart === false &&
        sr.nearDuplicate?.addedToShoppingCart === false
      ) {
        searchCopy.isPinned = false;
      }
      return searchCopy;
    };
    setSessionSearches((prev) => prev.map((s) => updateSearch(s)));
    if (currentSessionSearch.id === id) {
      setCurrentSessionSearch((prev) => updateSearch(prev));
    }
  };

  const onChangeNavigateAction = () => {
    const payload = {
      searches: sessionSearches,
      currentId: currentSessionSearch.id,
      projectId,
      userRoleId,
      userSecurityGroupId: userSecurityGroup,
    };
    sessionStorage.setItem('presistence-search', JSON.stringify(payload));
  };

  const renderRightPanel = () => {
    const resultMode =
      currentSessionSearch.lastRuntime || currentSessionSearch.advancedSearchId;
    const selectedSearchIds = sessionSearches
      .filter((s) => s.isPinned && s.advancedSearchId)
      .map((s) => s.advancedSearchId as number);
    if (resultMode) {
      return (
        <ResultShoppingCart
          searchId={currentSessionSearch.advancedSearchId}
          searchIds={selectedSearchIds}
          userRoleId={userRoleId}
          currentSessionId={currentSessionSearch.advancedSearchId}
          onItemRemoved={handleResultRemoved}
          onChangeNavigate={onChangeNavigateAction}
        />
      );
    } else {
      return (
        <SearchModifiers
          userRoleId={userRoleId}
          item={currentSessionSearch}
          onChange={handleSearchModifiersChanged}
        />
      );
    }
  };

  const handleItemSaved = (item: SessionSearch) => {
    updateItemState(item);
    setIsSaveDrawerOpen(false);
    setShowSaveSuccessToast(true);
  };

  return (
    <>
      <Grid container className="session-search-page">
        {/* Left Panel */}
        <Grid container size={3} className="session-search-left-panel">
          <Stack direction="column" sx={{width: '100%'}}>
            <Box className="session-search-left-header-panel">
              <LeftHeader />
            </Box>
            <Box className="session-search-search-list-panel">
              <SearchList
                items={displaySearches}
                canPin={canPinItem}
                onItemSelected={handleItemSelected}
                onItemPinned={handleItemPinned}
                onItemUnpinned={handleItemUnpinned}
                onItemDeleted={handleItemDeleted}
              />
            </Box>
          </Stack>
        </Grid>
        {/* Center Panel */}
        <Grid container size={6} className="session-search-center-panel">
          <Stack direction="column" sx={{width: '100%'}}>
            <Box className="session-search-center-header-panel">
              <CenterHeader
                uniqueDocIdCount={uniqueDocIdCount}
                canAdd={canAddItem}
                canDiscard={canDiscardItem}
                onAdd={handleItemAdded}
                onDiscardChanges={handleItemDiscarded}
              />
            </Box>
            <Box className="session-search-panel">
              <SearchQuery
                key={currentSessionSearch.id}
                userRoleId={userRoleId}
                item={currentSessionSearch}
                canEdit={canDiscardItem}
                onCopy={handleCopy}
                onSave={handleSave}
                onSearch={handleSearch}
                onModify={handleModify}
                onIndexChange={handleIndexChange}
                onAddAnalyticsQuery={handleItemModified}
                onAddConceptualQuery={handleItemModified}
                onAddContentAndMetadataQuery={handleItemModified}
                onAddWorkProductQuery={handleItemModified}
                onQueryReordered={handleItemModified}
                onQueryDeleted={handleItemModified}
                onQueryModified={handleItemModified}
                OnAddToShoppingCart={handleAddToShoppingCart}
              />
            </Box>
          </Stack>
        </Grid>
        {/* Right Panel */}
        <Grid size={3} className="session-search-right-panel">
          {renderRightPanel()}
        </Grid>
      </Grid>
      {showAnalyticWorProductValidation && (
        <ArrModal
          showModal={showAnalyticWorProductValidation}
          onClose={() => setShowAnalyticWorProductValidation(false)}
          title={t('sessionSearch.searchValidationErrorTitle')}
          description={criticalValidationError}
          showClose={false}
          descriptionHasHtml={errorStringContainsHtml}
          dataTestId="show-analyticswork-modal"
          buttons={[
            {
              id: '1',
              label: t('sessionSearch.ok'),
              onClick: () => {
                setShowAnalyticWorProductValidation(false);
              },
              variant: 'primary',
              dataTestId: 'sam-ok-button',
            },
          ]}
        />
      )}
      <ArrModal
        showModal={nestedChildModal}
        onClose={() => {
          setNestedChildModal(false);
          queueRequestRef.current = null;
        }}
        showClose
        title={t('sessionSearch.nestedSearchModal.title')}
        description={t('sessionSearch.nestedSearchModal.description')}
        titleStartIcon={
          <InfoIcon
            className="info-icon nested-modal-title-icon"
            aria-label={t('sessionSearch.nestedSearchModal.iconAriaLabel')}
          />
        }
        dataTestId="nested-child-modal"
        buttons={[
          {
            id: '1',
            label: t('sessionSearch.nestedSearchModal.cancelButton'),
            onClick: () => {
              setNestedChildModal(false);
              queueRequestRef.current = null;
            },
            variant: 'secondary',
            dataTestId: 'ncm-cancel-button',
          },
          {
            id: '2',
            label: t('sessionSearch.nestedSearchModal.proceedButton'),
            onClick: () => {
              void handleAddSearchToQueue();
            },
            variant: 'primary',
            dataTestId: 'ncm-proceed-button',
          },
        ]}
      />
      <ArrModal
        showModal={openNestedSearchModal}
        onClose={() => {
          setOpenNestedSearchModal(false);
          queueRequestRef.current = null;
        }}
        className="nested-search-execute-modal"
        title={t('savedSearch.executeNestedSearch.title')}
        ariaLabel={t('savedSearch.executeNestedSearch.title')}
        ariaDescription={t('savedSearch.executeNestedSearch.description')}
        dataTestId="nested-search-execute-modal"
        hideDefaultActions
        showClose={false}
      >
        <Box className="nested-search-execute-modal__body">
          <ArrBanner
            type="contextual"
            variant="complete"
            bannerContent={t(
              'savedSearch.executeNestedSearch.nestedInfoContent'
            )}
            startIcon={<CompleteIcon />}
            dataTestId="session-search-banner"
          />
          <div
            className="text-description-container"
            data-testid="view-session-search-nested-modal-content"
          >
            <Typography
              className="typo-text body3_size modal-description"
              data-testid="nested-search-modal-text"
            >
              {t('savedSearch.executeNestedSearch.nestedModalContent', {
                count: rows.length,
              })}
            </Typography>
          </div>
          <Box
            className={`nested-child-grid${nestedGridScrollable ? ' nested-child-grid--scrollable' : ''}`}
            data-testid="nested-child-table"
          >
            <ArrDataGrid
              hideFooter
              rowHeight={DEFAULT_ROW_HEIGHT}
              rows={nestedGridRows}
              columns={nestedColumns}
              data-testId="sessionsearch-nestedgrid"
              primaryKey="searchID"
              className="nested-table-grid"
              getRowId={(row) => row.searchID}
              sx={{height: nestedGridHeight, maxHeight: '100%'}}
            />
          </Box>
          <Box className="nested-modal-action-buttons">
            <ArrButton
              onClick={() => {
                setOpenNestedSearchModal(false);
                queueRequestRef.current = null;
              }}
              variant="secondary"
              size="small"
              dataTestId="view-sesssion-cancel-btn"
              aria-label={t('common.cancel')}
              className="modal-action-button nested-modal-cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <Box className="nested-modal-button">
              {/* todo */}
              <ArrButton
                onClick={() => {
                  void handleAddSearchToQueue();
                }}
                variant="primary"
                size="small"
                dataTestId="session-proceed-btn"
                type="submit"
                aria-label={t('common.proceed')}
                className="modal-action-button"
              >
                {t('common.proceed')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      </ArrModal>
      {showContentMDValidation && (
        <ArrModal
          showModal={showContentMDValidation}
          onClose={() => setShowContentMDValidation(false)}
          title={t('sessionSearch.searchValidationErrorTitle')}
          description={contentMDValidationError}
          showClose={false}
          descriptionHasHtml={errorStringContainsHtml}
          dataTestId="show-content-mdvalidation-modal"
          buttons={[
            {
              id: '1',
              label: t('sessionSearch.no'),
              onClick: () => {
                setShowContentMDValidation(false);
              },
              variant: 'primary',
              dataTestId: 'scm-no-button',
            },
            {
              id: '2',
              label: t('sessionSearch.yes'),
              onClick: () => {
                setcanContinueWithSearch(true);
                setShowContentMDValidation(false);
              },
              variant: 'primary',
              dataTestId: 'scm-yes-button',
            },
          ]}
        >
          <Box className="modal-desc-text" data-testid="arr-modal-description">
            <p
              data-testid={`view-session-search-content-mdvalidation-question`}
            >
              {t('sessionSearch.ContentMDValidationQuestion')}
            </p>
            <p
              data-testid={`view-session-search-content-mdvalidation-instruction`}
            >
              {t('sessionSearch.ContentMDValidationInstruction')}
            </p>
          </Box>
        </ArrModal>
      )}
      {showSearchToast &&
        searchToastMessages.map((toast) => (
          <ArrToastNotification
            key={toast.id}
            message={toast.message}
            onClose={() => {
              setSearchToastMessages((prev) => {
                const next = prev.filter((item) => item.id !== toast.id);
                if (next.length === 0) {
                  setShowSearchToast(false);
                }
                return next;
              });
            }}
            status={[toastType]}
          />
        ))}
      {(searchIsInProgress || isRunSearchLoading || isAddToQueueLoading) && (
        <ArrLoadingSpinner
          show={searchIsInProgress || isRunSearchLoading || isAddToQueueLoading}
          overlay={true}
          isOpen={
            searchIsInProgress || isRunSearchLoading || isAddToQueueLoading
          }
        />
      )}
      {isSaveDrawerOpen && (
        <SaveSearchDrawer
          item={currentSessionSearch}
          isOpen={isSaveDrawerOpen}
          savedSearchLimit={savedSearchLimit}
          onClose={() => setIsSaveDrawerOpen(false)}
          onSave={handleItemSaved}
          selectedIndexData={selectedIndexData}
        />
      )}
      {showSaveSuccessToast && (
        <ArrToastNotification
          message={t('sessionSearch.saveDrawer.saveSuccess')}
          onClose={() => setShowSaveSuccessToast(false)}
          status={[ToastType.Success]}
        />
      )}
      {showPinToast && (
        <ArrToastNotification
          message={t('sessionSearch.listItems.pinWarning')}
          status={[ToastType.Warning]}
          onClose={() => setShowPinToast(false)}
        />
      )}
      {showSearchWaitModal && (
        <ArrSearchProgressDialog
          showModal={showSearchWaitModal}
          title={t('common.arrSearchProgressDialog.title')}
          description={t('common.arrSearchProgressDialog.description')}
          buttons={[
            {
              id: '1',
              label: t('common.arrSearchProgressDialog.waitForAllResults'),
              onClick: handleWaitForAllResults,
              variant: 'primary',
              dataTestId: 'arr-search-wait-button',
            },
            {
              id: '2',
              label: t(
                'common.arrSearchProgressDialog.notifyWhenPureHitAreDone'
              ),
              onClick: handleNotifyPureHits,
              variant: 'secondary',
              dataTestId: 'arr-search-notifywhenphdone-button',
            },
            {
              id: '3',
              label: t(
                'common.arrSearchProgressDialog.notifyMeWhenResultsAreDone'
              ),
              onClick: handleNotifyAll,
              variant: 'secondary',
              dataTestId: 'arr-search-notifyme-button',
            },
          ]}
          onClose={() => setShowSearchWaitModal(false)}
        ></ArrSearchProgressDialog>
      )}
    </>
  );
};

export default ViewSessionSearch;
